import { ChecklistType } from 'src/components/checklists/types';
import { getRootProviderId } from 'src/config/config';
import { ChecklistApprovalType, ChecklistItemState, ChecklistStatus, ChecklistWithItemStates } from 'src/types/classic';

import { ChecklistIdInput } from './types';

export const makeTypeBasedVenueChecklistParams = ({ identifier, type }: ChecklistIdInput) => {
  switch (type) {
    case ChecklistType.ORGANISATION:
      return { VenueID: identifier, EntityID: null };
    case ChecklistType.USER:
      return { VenueID: getRootProviderId(), EntityID: identifier };
    default:
      return {};
  }
};

const isStatusReviewable = (status: ChecklistStatus) =>
  [ChecklistStatus.AWAITING_REVIEW, ChecklistStatus.REJECTED, ChecklistStatus.COMPLETE].includes(status);

export function canChecklistBeReviewed(checklist: ChecklistWithItemStates) {
  const {
    Checklist: { ApprovalType: approvalType },
    Status: status,
  } = checklist.ChecklistState;

  return approvalType === ChecklistApprovalType.MANUAL && isStatusReviewable(status);
}

export function canItemBeReviewed(state: ChecklistItemState, options: { canUserOverride: boolean }) {
  const { canUserOverride } = options;
  const {
    ChecklistItem: { ApprovalType: approvalType, StatusOverrideEnabled: statusOverrideEnabled },
    Status: status,
  } = state;

  return (
    (canUserOverride && statusOverrideEnabled) ||
    (approvalType === ChecklistApprovalType.MANUAL && isStatusReviewable(status))
  );
}

export function calculateCompletion(checklistItems: ChecklistItemState[] = []): number {
  if (!checklistItems?.length) return 0;

  let completedItems = 0;

  for (const item of checklistItems) {
    if (item.Status === ChecklistStatus.COMPLETE) {
      completedItems++;
    }
  }

  return Math.round((completedItems / checklistItems.length) * 100);
}

export const convertBytesToKB = (bytes: number | undefined): number => {
  if (!bytes) return 0;
  return Math.round(bytes / 1000);
};

const checklistStatusMapping = {
  [ChecklistStatus.NOT_STARTED]: {
    checklist: {
      color: '#707485',
      text: 'not started',
    },
    checklistItem: {
      color: 'neutral2',
      text: 'not started',
    },
  },
  [ChecklistStatus.INCOMPLETE]: {
    checklist: {
      color: '#eab565',
      text: 'in progress',
    },
    checklistItem: {
      color: 'primary',
      text: 'incomplete',
    },
  },
  [ChecklistStatus.AWAITING_REVIEW]: {
    checklist: {
      color: '#e6854d',
      text: 'awaiting approval',
    },
    checklistItem: {
      color: 'warning',
      text: 'awaiting review',
    },
  },
  [ChecklistStatus.COMPLETE]: {
    checklist: {
      color: '#5cbbb1',
      text: 'approved',
    },
    checklistItem: {
      color: 'success',
      text: 'complete',
    },
  },
  [ChecklistStatus.REJECTED]: {
    checklist: {
      color: '#d42f42',
      text: 'declined',
    },
    checklistItem: {
      color: 'error',
      text: 'rejected',
    },
  },
} as const;

const autoApprovalStatusOverrides = {
  [ChecklistStatus.AWAITING_REVIEW]: {
    checklistItem: {
      text: 'in progress',
    },
  },
} as const;

export const getChecklistStatusTagValues = ({
  status,
  type,
  approvalType,
}: {
  status: ChecklistStatus;
  type: 'checklist' | 'checklistItem';
  approvalType: ChecklistApprovalType;
}) => {
  const defaultValues = checklistStatusMapping?.[status]?.[type] ?? {};

  if (approvalType === ChecklistApprovalType.AUTO) {
    const { color = defaultValues.color, text = defaultValues.text } =
      autoApprovalStatusOverrides?.[status]?.[type] ?? {};

    return { color, text };
  }

  return defaultValues;
};
